import { mergeAttributes, Node, Extension, type JSONContent } from '@tiptap/core';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, type Editor, type NodeViewProps } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useQuery } from '@tanstack/react-query';
import { Bold, Code, Heading2, Italic, List, ListChecks, ListOrdered, Quote } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import { useTeams, useUsers } from '@/lib/queries';
import type { Item } from '@/lib/types';
import { cn } from '@/lib/utils';
import { StatusBadge } from './item-bits';

const BLOCK_TYPES = ['paragraph', 'heading', 'taskItem', 'listItem', 'codeBlock', 'blockquote'];
const newId = () => `b${Math.random().toString(16).slice(2, 14)}`;

/** Stable block ids on every block (§7.12) so notes can reference lines for assignments, items and checklist sync. */
const BlockId = Extension.create({
  name: 'blockId',
  addGlobalAttributes() {
    return [
      {
        types: BLOCK_TYPES,
        attributes: {
          blockId: {
            default: null,
            parseHTML: (el) => el.getAttribute('data-block-id'),
            renderHTML: (attrs) => (attrs.blockId ? { 'data-block-id': attrs.blockId } : {}),
          },
        },
      },
    ];
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('blockIdAssign'),
        appendTransaction: (trs, _old, state) => {
          if (!trs.some((t) => t.docChanged)) return null;
          const seen = new Set<string>();
          let tr = state.tr;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (!BLOCK_TYPES.includes(node.type.name)) return;
            const id = node.attrs.blockId as string | null;
            if (!id || seen.has(id)) {
              tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockId: newId() });
              changed = true;
            } else seen.add(id);
          });
          return changed ? tr.setMeta('addToHistory', false) : null;
        },
      }),
    ];
  },
});

/** Live work item chip (FR-NOTE-11): key, title, assignee, status — fetched live. */
function ChipView({ node }: NodeViewProps) {
  const key = node.attrs.itemKey as string;
  const { data } = useQuery({ queryKey: ['item', key, 'chip'], queryFn: () => http.get<Item>(`/items/${key}`), staleTime: 30_000, retry: false });
  return (
    <NodeViewWrapper as="span" className="inline">
      <Link to={`/items/${key}`} className="item-chip" contentEditable={false}>
        <span className="font-semibold text-brand">{key}</span>
        <span className={cn('max-w-[220px] truncate', data?.status.category === 'done' && 'line-through text-subtle')}>{data?.title ?? (node.attrs.title as string)}</span>
        {data && <StatusBadge status={data.status} compact />}
      </Link>
    </NodeViewWrapper>
  );
}

export const WorkItemChip = Node.create({
  name: 'workItemChip',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes: () => ({ itemKey: { default: null }, itemId: { default: null }, title: { default: '' } }),
  parseHTML: () => [{ tag: 'span[data-item-key]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', mergeAttributes({ 'data-item-key': HTMLAttributes.itemKey, class: 'item-chip' }), HTMLAttributes.itemKey],
  addNodeView: () => ReactNodeViewRenderer(ChipView),
});

interface MentionItem {
  id: string;
  label: string;
  kind: 'user' | 'group';
}

/** Minimal suggestion popup for @mentions (no external popper). */
function mentionSuggestion(getItems: () => MentionItem[]) {
  return {
    items: ({ query }: { query: string }) =>
      getItems()
        .filter((i) => i.label.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8),
    render: () => {
      let el: HTMLDivElement | null = null;
      let items: MentionItem[] = [];
      let index = 0;
      let command: ((i: MentionItem) => void) | null = null;
      const paint = () => {
        if (!el) return;
        el.innerHTML = '';
        items.forEach((it, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = `block w-full rounded px-2 py-1 text-left text-sm ${i === index ? 'bg-muted' : ''}`;
          b.textContent = it.kind === 'group' ? `@${it.label} (team)` : it.label;
          b.onmousedown = (e) => {
            e.preventDefault();
            command?.(it);
          };
          el!.appendChild(b);
        });
        if (!items.length) el.innerHTML = '<div class="px-2 py-1 text-xs text-subtle">No matches</div>';
      };
      const place = (rect: DOMRect | null) => {
        if (!el || !rect) return;
        el.style.left = `${rect.left + window.scrollX}px`;
        el.style.top = `${rect.bottom + window.scrollY + 4}px`;
      };
      return {
        onStart: (props: { items: MentionItem[]; command: (i: { id: string; label: string; kind: string }) => void; clientRect?: (() => DOMRect | null) | null }) => {
          mentionMenuOpen = true;
          el = document.createElement('div');
          el.className = 'fixed z-[60] w-56 rounded-md border border-border bg-surface p-1 shadow-lg';
          document.body.appendChild(el);
          items = props.items;
          command = (i) => props.command({ id: i.id, label: i.label, kind: i.kind });
          index = 0;
          paint();
          place(props.clientRect?.() ?? null);
        },
        onUpdate: (props: { items: MentionItem[]; command: (i: { id: string; label: string; kind: string }) => void; clientRect?: (() => DOMRect | null) | null }) => {
          items = props.items;
          command = (i) => props.command({ id: i.id, label: i.label, kind: i.kind });
          index = Math.min(index, Math.max(0, items.length - 1));
          paint();
          place(props.clientRect?.() ?? null);
        },
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (event.key === 'ArrowDown') index = (index + 1) % Math.max(1, items.length);
          else if (event.key === 'ArrowUp') index = (index - 1 + items.length) % Math.max(1, items.length);
          else if (event.key === 'Enter' || event.key === 'Tab') {
            if (items[index]) command?.(items[index]);
            return true;
          } else return false;
          paint();
          return true;
        },
        onExit: () => {
          mentionMenuOpen = false;
          el?.remove();
          el = null;
        },
      };
    },
  };
}

/** True while the @mention menu is open, so Enter picks a person instead of sending. */
let mentionMenuOpen = false;

export interface RichEditorProps {
  value: JSONContent | null | undefined;
  onChange?: (doc: JSONContent) => void;
  editable?: boolean;
  placeholder?: string;
  className?: string;
  minimal?: boolean;
  autofocus?: boolean;
  onEditor?: (e: Editor) => void;
  onSubmit?: () => void;
  /** Chat composers: Enter sends, Shift+Enter adds a line (Cmd/Ctrl+Enter always sends). */
  enterSubmits?: boolean;
}

/** TipTap editor (§14): headings, lists, checklists, code, links, @mentions, work item chips. */
export function RichEditor({ value, onChange, editable = true, placeholder, className, minimal, autofocus, onEditor, onSubmit, enterSubmits }: RichEditorProps) {
  const { data: users } = useUsers();
  const { data: teams } = useTeams();
  const mentionItems = useRef<MentionItem[]>([]);
  mentionItems.current = useMemo(
    () => [...(users ?? []).map((u) => ({ id: u.id, label: u.displayName, kind: 'user' as const })), ...(teams ?? []).map((t) => ({ id: t.handle, label: t.handle, kind: 'group' as const }))],
    [users, teams],
  );
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const enterRef = useRef(enterSubmits);
  enterRef.current = enterSubmits;
  const editor = useEditor({
    // Create the editor after mount: React node views (item chips) must not render during React's render phase.
    immediatelyRender: false,
    editable,
    autofocus,
    extensions: [
      StarterKit.configure({ link: { openOnClick: !editable, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write something… Use @ to mention' }),
      Mention.extend({
        addAttributes() {
          return { ...this.parent?.(), kind: { default: 'user' } };
        },
      }).configure({ HTMLAttributes: { class: 'mention' }, suggestion: mentionSuggestion(() => mentionItems.current) as never }),
      WorkItemChip,
      BlockId,
      Extension.create({
        name: 'submitShortcut',
        addKeyboardShortcuts: () => ({
          'Mod-Enter': () => (submitRef.current ? (submitRef.current(), true) : false),
          Enter: () => (enterRef.current && !mentionMenuOpen && submitRef.current ? (submitRef.current(), true) : false),
        }),
      }),
    ],
    content: (value as JSONContent) ?? '',
    onUpdate: ({ editor: e }) => onChange?.(e.getJSON()),
    editorProps: { attributes: { class: cn('prose-kbx focus:outline-none', !minimal && 'min-h-[120px]') } },
  });

  useEffect(() => {
    if (editor && onEditor) onEditor(editor);
  }, [editor, onEditor]);

  // Accept external updates (e.g. realtime checklist sync) when the content actually differs.
  useEffect(() => {
    if (!editor || !value) return;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(value) && !editor.isFocused) {
      queueMicrotask(() => !editor.isDestroyed && editor.commands.setContent(value as JSONContent, { emitUpdate: false }));
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;
  return (
    <div className={cn(editable && 'rounded-md border border-border bg-surface focus-within:border-brand', className)}>
      {editable && !minimal && <Toolbar editor={editor} />}
      <EditorContent editor={editor} className={cn(editable && 'px-3 py-2')} />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const btn = (active: boolean, onClick: () => void, icon: React.ReactNode, label: string) => (
    <button type="button" title={label} aria-label={label} onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={cn('rounded p-1 text-subtle hover:bg-muted', active && 'bg-muted text-fg')}>
      {icon}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-0.5 border-b border-border px-1.5 py-1">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold className="h-3.5 w-3.5" />, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic className="h-3.5 w-3.5" />, 'Italic')}
      {btn(editor.isActive('code'), () => editor.chain().focus().toggleCode().run(), <Code className="h-3.5 w-3.5" />, 'Code')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 className="h-3.5 w-3.5" />, 'Heading')}
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List className="h-3.5 w-3.5" />, 'Bullet list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered className="h-3.5 w-3.5" />, 'Numbered list')}
      {btn(editor.isActive('taskList'), () => editor.chain().focus().toggleTaskList().run(), <ListChecks className="h-3.5 w-3.5" />, 'Checklist')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), <Quote className="h-3.5 w-3.5" />, 'Quote')}
    </div>
  );
}

export function RichViewer({ value, className }: { value: unknown; className?: string }) {
  if (!value || (typeof value === 'object' && !(value as JSONContent).content?.length)) return <div className={cn('text-sm text-subtle', className)}>—</div>;
  return <RichEditor value={value as JSONContent} editable={false} className={className} minimal />;
}

export const isEmptyDoc = (d: JSONContent | null | undefined) => !d || !JSON.stringify(d).match(/"text":"[^"]*\S/) && !JSON.stringify(d).includes('workItemChip') && !JSON.stringify(d).includes('mention');
